import { useState, useMemo, useEffect } from 'react';
import { motion } from 'motion/react';
import { PropertyFilters } from './PropertyFilters';
import { Property, PropertyFilters as FilterType } from '../types/property';
import { propertyAPI } from '../lib/property-api';
import { logger } from '../services/logger.service';
import { Home, Loader, Heart, MapPin, Bed, Bath, Ruler, ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const ITEMS_PER_PAGE = 30;

interface PropertyCatalogProps {
  onPropertySelect: (property: Property) => void;
  /** When true, only show saved/favorited properties */
  showSavedOnly?: boolean;
  /** Set of favorite property IDs */
  favorites?: Set<string>;
  /** Callback to toggle a property favorite */
  onToggleFavorite?: (propertyId: string) => void;
}

// Helper to safely extract property ID from various formats
function getPropertyId(p: any): string {
  return p?.somethingPropertyId || p?.listingId || p?.id || String(Math.random());
}

// Helper to safely extract property title
function getPropertyTitle(p: any): string {
  return p?.marketingHeading || p?.title || p?.name || 'Untitled Property';
}

// Helper to safely extract property price
function getPropertyPrice(p: any): number {
  const price = p?.listPrice || p?.price;
  if (typeof price === 'string') return parseInt(price, 10) || 0;
  if (typeof price === 'number') return price;
  return 0;
}

// Helper to format price
function formatPrice(price: number): string {
  if (!price) return 'Price on Request';
  return `R ${price.toLocaleString()}`;
}

// Helper to extract location string — never returns street address (security)
function getLocationString(p: any): string {
  // Lambda format: location object — use suburb/city only
  if (p?.location && typeof p.location === 'object') {
    const parts = [p.location.suburb, p.location.city].filter(Boolean);
    return parts.join(', ');
  }

  // Internal format: address object — use suburb/city only
  if (p?.address) {
    const parts = [p.address.suburb, p.address.city].filter(Boolean);
    return parts.join(', ');
  }

  return '';
}

// Helper to get bedrooms count
function getBedrooms(p: any): number | null {
  if (typeof p?.bedrooms === 'number') return p.bedrooms;
  if (p?.facts?.bedrooms) return p.facts.bedrooms;
  // Lambda format: features array
  if (Array.isArray(p?.features)) {
    const bed = p.features.find((f: any) =>
      f?.type?.toLowerCase().includes('bedroom') ||
      f?.description?.toLowerCase().includes('bedroom')
    );
    if (bed?.value) return bed.value;
  }
  return null;
}

// Helper to get bathrooms count
function getBathrooms(p: any): number | null {
  if (typeof p?.bathrooms === 'number') return p.bathrooms;
  if (p?.facts?.bathrooms) return p.facts.bathrooms;
  // Lambda format: features array
  if (Array.isArray(p?.features)) {
    const bath = p.features.find((f: any) =>
      f?.type?.toLowerCase().includes('bathroom') ||
      f?.description?.toLowerCase().includes('bathroom')
    );
    if (bath?.value) return bath.value;
  }
  return null;
}

// Helper to get area
function getArea(p: any): number | null {
  if (typeof p?.area === 'number') return p.area;
  if (typeof p?.squareMeters === 'number') return p.squareMeters;
  if (p?.facts?.livingArea) return p.facts.livingArea;
  if (p?.floorArea?.size) return p.floorArea.size;
  return null;
}

// Helper to get images array
function getImages(p: any): string[] {
  if (Array.isArray(p?.images)) {
    return p.images.map((img: any) =>
      typeof img === 'string' ? img : img?.url || ''
    ).filter(Boolean);
  }
  if (Array.isArray(p?.media)) {
    return p.media.filter((m: any) => m?.type === 'image').map((m: any) => m?.url || '');
  }
  return [];
}

// Helper to get property type
function getPropertyType(p: any): string {
  return p?.propertyType || p?.type || '';
}

// Helper to get description
function getDescription(p: any): string {
  return p?.marketingDescription || p?.description || '';
}

// Helper to get listing status
function getListingStatus(p: any): string {
  return p?.listingStatus || p?.status || 'Active';
}

// Normalize any property format to the internal Property interface
// This ensures PropertyDetail receives a properly structured object
function normalizeProperty(p: any): Property {
  const id = getPropertyId(p);
  const title = getPropertyTitle(p);
  const price = getPropertyPrice(p);
  const location = getLocationString(p);
  const bedrooms = getBedrooms(p);
  const bathrooms = getBathrooms(p);
  const area = getArea(p);
  const images = getImages(p);
  const propertyType = getPropertyType(p);
  const description = getDescription(p);
  const listingStatus = getListingStatus(p);

  // Parse location into address parts
  const locationParts = location.split(',').map(s => s.trim());
  const suburb = locationParts[0] || '';
  const city = locationParts[1] || locationParts[0] || '';

  // Build media array from images
  const media = images.map((url, idx) => ({
    id: `media-${idx}`,
    url,
    alt: `${title} - Image ${idx + 1}`,
    isCover: idx === 0,
    order: idx
  }));

  // Extract agent info — prefer denormalized flat fields from API (agentName etc.)
  // then fall back to nested agent object, then compositeAgentIds
  const hasAgentData = p?.agentName || p?.agent || p?.agents?.[0];
  const agent = hasAgentData ? {
    id: p.agent?.id || p.agents?.[0]?.id || p.agentId || '',
    name: p.agentName || p.agent?.name || p.agents?.[0]?.name || '',
    email: p.agentEmail || p.agent?.email || p.agents?.[0]?.email || '',
    phone: p.agentPhone || p.agent?.phone || p.agents?.[0]?.phone || '',
    whatsapp: p.agentPhone || p.agent?.whatsapp || p.agents?.[0]?.phone || '',
    avatarUrl: p.agentPhotoUrl || p.agent?.avatarUrl || p.agents?.[0]?.avatarUrl || undefined,
  } : undefined;

  return {
    id,
    slug: p?.slug || id,
    title,
    type: (propertyType || 'House') as Property['type'],
    status: 'Published' as Property['status'],
    listingStatus: listingStatus as Property['listingStatus'],
    somethingPropertyId: p?.somethingPropertyId || p?.listingId,
    externalId: p?.externalId || p?.listingId,
    address: {
      street: p?.address?.street || location,
      suburb: p?.address?.suburb || suburb,
      city: p?.address?.city || city,
      province: p?.address?.province || '',
      postalCode: p?.address?.postalCode || ''
    },
    geo: {
      lat: p?.geo?.lat || p?.coordinates?.lat || 0,
      lng: p?.geo?.lng || p?.coordinates?.lng || 0
    },
    price,
    facts: {
      bedrooms: bedrooms ?? undefined,
      bathrooms: bathrooms ?? undefined,
      parking: p?.facts?.parking || p?.parking || undefined,
      erfSize: p?.facts?.erfSize || p?.erfSize || undefined,
      floorSize: p?.facts?.floorSize || area || undefined
    },
    amenities: p?.amenities || [],
    media,
    description,
    listingDate: p?.listingDate || p?.createdAt || new Date().toISOString(),
    agentId: p?.agentId || agent?.id || '',
    agent,
    externalAgentId: p?.externalAgentId,
    visibility: 'Public' as Property['visibility'],
    flags: {
      featured: p?.flags?.featured || p?.featured || false,
      new: p?.flags?.new || p?.isNew || false
    },
    noTransferDuty: p?.noTransferDuty || false,
    priceReduced: p?.priceReduced || false,
    rates: p?.rates,
    levy: p?.levy,
    mandateType: p?.mandateType,
    features: p?.features,
    compositeAgentIds: p?.compositeAgentIds
  };
}

export function PropertyCatalog({ onPropertySelect, showSavedOnly, favorites, onToggleFavorite }: PropertyCatalogProps) {
  const [filters, setFilters] = useState<FilterType>({});
  const [properties, setProperties] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Server-side pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 30,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load properties when page, search, sort, or filter values change
  // F-034 real fix: type and listingStatus now sent to API as query params
  useEffect(() => {
    loadProperties(currentPage, debouncedSearch, filters.sort, filters.bedrooms, filters.bathrooms, filters.minPrice, filters.maxPrice, filters.type, filters.listingStatus);
  }, [currentPage, debouncedSearch, filters.sort, filters.bedrooms, filters.bathrooms, filters.minPrice, filters.maxPrice, filters.type, filters.listingStatus]);

  const loadProperties = async (page: number = 1, search: string = '', sort?: string, minBedrooms?: number, minBathrooms?: number, priceMin?: number, priceMax?: number, propertyType?: string, listingStatus?: string) => {
    try {
      setIsLoading(true);

      // Get sort order from argument (not stale closure)
      const sortBy = sort === 'price_asc' ? 'price_asc'
        : sort === 'price_desc' ? 'price_desc'
        : sort === 'bedrooms_desc' ? 'bedrooms_desc'
        : sort === 'bathrooms_desc' ? 'bathrooms_desc'
        : 'newest';

      // Fetch properties from API with server-side pagination + filter params
      const result = await propertyAPI.getProperties({
        page,
        pageSize: 30,
        search: search || undefined,
        sortBy,
        minBedrooms: minBedrooms || undefined,
        minBathrooms: minBathrooms || undefined,
        priceMin: priceMin || undefined,
        priceMax: priceMax || undefined,
        propertyType: propertyType || undefined,
        listingStatus: listingStatus || undefined,
      });

      const listings = Array.isArray(result?.listings) ? result.listings : [];
      console.log('[PropertyCatalog] Loaded', listings.length, 'properties, page', result?.pagination?.page, 'of', result?.pagination?.totalPages);

      setProperties(listings);
      setPagination(result?.pagination || { page: 1, pageSize: 30, totalItems: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false });
    } catch (error) {
      console.error('[PropertyCatalog] Error loading properties:', error);
      logger.error('Failed to load properties', error as Error, undefined, 'PropertyCatalog');
      setProperties([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Client-side filtering for favorites, type, and listingStatus
  // Search, sort, bedrooms, bathrooms, price are handled server-side
  const filteredProperties = useMemo(() => {
    if (!Array.isArray(properties)) {
      return [];
    }

    let results = properties.filter(p => p !== null && p !== undefined);

    // Deduplicate by unique ID (safety measure)
    const seen = new Set<string>();
    results = results.filter(p => {
      const uid = p?.somethingPropertyId || p?.externalId || p?.listingId || p?.id;
      if (!uid) return true;
      if (seen.has(uid)) return false;
      seen.add(uid);
      return true;
    });

    // F-034 fix: client-side type filter (fallback if server doesn't filter by type)
    if (filters.type) {
      results = results.filter(p => {
        const pType = getPropertyType(p);
        return pType?.toLowerCase() === filters.type?.toLowerCase();
      });
    }

    // F-034 fix: client-side listingStatus filter (fallback if server doesn't filter by status)
    if (filters.listingStatus) {
      results = results.filter(p => {
        const pStatus = p?.listingStatus || p?.status;
        return pStatus?.toLowerCase() === filters.listingStatus?.toLowerCase();
      });
    }

    // If in saved mode, filter to only favorites (client-side only)
    if (showSavedOnly && favorites) {
      results = results.filter(p => favorites.has(getPropertyId(p)));
    }

    return results;
  }, [properties, showSavedOnly, favorites, filters.type, filters.listingStatus]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearch]);

  // Handle filter changes (update search from filters)
  useEffect(() => {
    if (filters.search !== searchQuery) {
      setSearchQuery(filters.search || '');
    }
  }, [filters.search]);

  // Reset to page 1 when sort or any filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [filters.sort, filters.bedrooms, filters.bathrooms, filters.minPrice, filters.maxPrice]);

  // Server-side pagination - properties are already paginated from API
  const totalPages = pagination.totalPages;
  const paginatedProperties = filteredProperties; // Already paginated from server

  // Page navigation handlers
  const goToPage = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of results
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const goToPreviousPage = () => {
    if (pagination.hasPrevPage) goToPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (pagination.hasNextPage) goToPage(currentPage + 1);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      if (!pages.includes(totalPages) && totalPages > 0) pages.push(totalPages);
    }

    return pages;
  };

  // Render a property card - handles both Lambda and internal formats
  const renderPropertyCard = (property: any, index: number) => {
    const id = getPropertyId(property);
    const title = getPropertyTitle(property);
    const price = getPropertyPrice(property);
    const location = getLocationString(property);
    const bedrooms = getBedrooms(property);
    const bathrooms = getBathrooms(property);
    const area = getArea(property);
    const images = getImages(property);
    const propertyType = getPropertyType(property);
    const isFavorite = favorites?.has(id);

    return (
      <motion.div
        key={id}
        data-testid="property-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.6) }}
        className="group cursor-pointer"
        onClick={() => onPropertySelect(normalizeProperty(property))}
      >
        <div className="bg-card rounded-xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
          {/* Image */}
          <div className="relative aspect-[4/3] bg-muted overflow-hidden">
            {images.length > 0 ? (
              <img
                src={images[0]}
                alt={title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Home className="w-12 h-12 text-muted-foreground/30" />
              </div>
            )}

            {/* Property Type Badge */}
            {propertyType && (
              <Badge className="absolute top-3 left-3 bg-background/90 text-foreground">
                {propertyType}
              </Badge>
            )}

            {/* Favorite Button */}
            {onToggleFavorite && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(id);
                }}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-background/90 flex items-center justify-center hover:bg-background transition-colors"
              >
                <Heart
                  className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`}
                />
              </button>
            )}

            {/* Image Count */}
            {images.length > 1 && (
              <div className="absolute bottom-3 right-3 bg-background/90 px-2 py-1 rounded-md text-xs flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {images.length}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Price */}
            <div className="text-xl font-bold text-primary mb-2">
              {formatPrice(price)}
            </div>

            {/* Title */}
            <h3 className="font-semibold text-foreground line-clamp-1 mb-2">
              {title}
            </h3>

            {/* Location */}
            {location && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="line-clamp-1">{location}</span>
              </div>
            )}

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {bedrooms !== null && (
                <div className="flex items-center gap-1">
                  <Bed className="w-4 h-4" />
                  <span>{bedrooms}</span>
                </div>
              )}
              {bathrooms !== null && (
                <div className="flex items-center gap-1">
                  <Bath className="w-4 h-4" />
                  <span>{bathrooms}</span>
                </div>
              )}
              {area !== null && (
                <div className="flex items-center gap-1">
                  <Ruler className="w-4 h-4" />
                  <span>{area}m²</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(88,101,242,0.08),transparent_50%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6"
            >
              {showSavedOnly ? (
                <>
                  <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                  <span className="text-sm" data-testid="analytics-catalog-saved-properties-count">{filteredProperties.length} Saved Properties</span>
                </>
              ) : (
                <>
                  <Home className="w-4 h-4 text-primary" />
                  <span className="text-sm" data-testid="analytics-catalog-total-properties-count">Browse {pagination.totalItems || properties.length}+ Properties</span>
                </>
              )}
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              {showSavedOnly ? 'Your Saved Properties' : 'Find Your Perfect Property'}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {showSavedOnly
                ? 'Properties you\'ve saved for later. Click the heart to remove from your saved list.'
                : 'Discover exceptional properties across South Africa. From modern apartments to luxury homes, we have something for everyone.'}
            </p>
          </motion.div>

          {/* Filters */}
          {!showSavedOnly && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                resultsCount={filteredProperties.length}
              />
            </motion.div>
          )}
        </div>
      </section>

      {/* Results Grid */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center py-24"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6">
                <Loader className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h3 className="text-2xl font-semibold mb-2">Loading properties...</h3>
              <p className="text-muted-foreground">Please wait while we fetch the latest properties.</p>
            </motion.div>
          ) : filteredProperties.length > 0 ? (
            <>
              {/* Results info */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-muted-foreground">
                  Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems} properties
                </p>
                {totalPages > 1 && (
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                )}
              </div>

              {/* Properties Grid */}
              <motion.div
                key={currentPage} // Re-animate on page change
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {paginatedProperties.map((property, index) => renderPropertyCard(property, index))}
              </motion.div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12 pb-8">
                  {/* Previous Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((page, index) => (
                      typeof page === 'number' ? (
                        <Button
                          key={index}
                          variant={currentPage === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => goToPage(page)}
                          className="min-w-[40px]"
                        >
                          {page}
                        </Button>
                      ) : (
                        <span key={index} className="px-2 text-muted-foreground">
                          {page}
                        </span>
                      )
                    ))}
                  </div>

                  {/* Next Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center py-24"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6">
                {showSavedOnly ? (
                  <Heart className="w-10 h-10 text-muted-foreground" />
                ) : (
                  <Home className="w-10 h-10 text-primary" />
                )}
              </div>
              <h3 className="text-2xl font-semibold mb-2">
                {showSavedOnly ? "You haven't saved any properties yet" : 'No properties found'}
              </h3>
              <p className="text-muted-foreground">
                {showSavedOnly
                  ? 'Browse properties and click the heart icon to save them here.'
                  : 'Try adjusting your filters to see more results.'}
              </p>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
